import Backdrop from '@mui/material/Backdrop'
import Button from '@mui/material/Button'
import { PinturaEditor } from '@pqina/react-pintura'
import { ComposerMediaContext } from 'components/ComposerMediaContext/context'
import { ImageEditor } from 'components/ImageEditor'
import SimpleLoader from 'components/SimpleLoader'
import { useCallback, useContext, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { addComposerImages, promptCompose } from 'services/compose'
import { composerImageUrlsSelector, multiPostsArraySelector } from 'services/compose/selectors'
import { message } from 'services/snackbar'
import { setUploaderFiles, setUploadsDialogOpen } from 'services/uploads/actions'
import { StoreThunkDispatch } from 'store/state'
import styles from './EditTab.pcss'

const EMPTY_IMAGE_URL = `${window.location.origin}/static/img/pesdk/pesdk-empty.jpg`

interface EditTabProps {
  imageUrl?: string
  onClose?: () => void
}

export function EditTab(props: EditTabProps) {
  const { onClose } = props
  const editorRef = useRef<PinturaEditor>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const [isCreatingFile, setIsCreatingFile] = useState(false)
  const { dispatch: composerMediaDispatch } = useContext(ComposerMediaContext)
  const composerCarouselImages = useSelector(composerImageUrlsSelector)
  const multiPosts = useSelector(multiPostsArraySelector)

  const generateImage = useCallback(() => {
    return new Promise<File>((resolve, reject) => {
      if (!editorRef.current) {
        dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
        return reject('Editor not initialized!')
      }
      setIsCreatingFile(true)
      editorRef.current.editor.processImage()
        .then((result) => {
          setIsCreatingFile(false)
          resolve(result.dest)
        }).catch(() => {
          setIsCreatingFile(false)
          dispatch(message(intl.formatMessage({ id: 'general.media-editor.error' }), 'error'))
          reject('Error generating image.')
        })
    })
  }, [dispatch, intl])

  const saveToLibrary = useCallback(() => {
    dispatch(message(intl.formatMessage({ id: 'content-studio.notifications.creating-image' })))
    generateImage().then((file: File) => {
      dispatch(setUploaderFiles({ [file.name]: file }))
      dispatch(setUploadsDialogOpen(true))
    })
  }, [dispatch, generateImage, intl])

  const addToComposer = useCallback(() => {
    if (composerCarouselImages.length > 1 || multiPosts.length > 0) {
      dispatch(promptCompose({ content: {} as any, action: 'create' }))
      return
    }
    dispatch(message(intl.formatMessage({ id: 'content-studio.notifications.creating-image' })))
    generateImage().then((file: File) => {
      dispatch(addComposerImages({ items: [{ file, isEdited: true }], contextDispatch: composerMediaDispatch }))
      navigate('/composer', { state: { floating: true, modal: 'compose', opener: location.pathname, background: location } })
      if (onClose) {
        onClose()
      }
    })
  }, [composerCarouselImages.length, composerMediaDispatch, dispatch, generateImage, intl, location, multiPosts.length, navigate, onClose])

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <Button variant="text" disabled={isCreatingFile} onClick={saveToLibrary}>
          <FormattedMessage id="content-studio.actions.save-to-library" />
        </Button>
        <Button variant="contained" color="primary" disabled={isCreatingFile} onClick={addToComposer}>
          <FormattedMessage id="content-studio.actions.add-to-composer" />
        </Button>
      </div>
      <ImageEditor
        ref={editorRef}
        imageUrl={props.imageUrl || EMPTY_IMAGE_URL}
        className={styles.editor}
        hideSaveButton
      />
      <Backdrop open={isCreatingFile} className={styles.backdrop}>
        <SimpleLoader />
      </Backdrop>
    </div>
  )
}

export default EditTab
